import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
  canMutateAction,
  canSetActionStatus,
  getCorrectiveAction,
  isActionMutationLocked,
  linkEvidenceToAction,
  updateCorrectiveAction,
  type CorrectiveActionStatus,
} from '../../lib/services/correctiveActions'

const OWNER_ROLES = ['facility_admin', 'medical_director', 'nurse_manager', 'quality_coordinator']
const STATUSES: CorrectiveActionStatus[] = ['open', 'in_progress', 'completed', 'verified', 'overdue']

function formatToken(value: string | null | undefined): string {
  if (!value) return 'Unassigned'
  return value.replace(/_/g, ' ')
}

function formatAssignee(action: {
  assigned_to?: { email: string; role: string | null } | null
  assigned_to_user_id?: string | null
}): string {
  if (action.assigned_to?.email) {
    return action.assigned_to.role
      ? `${action.assigned_to.email} (${formatToken(action.assigned_to.role)})`
      : action.assigned_to.email
  }
  return action.assigned_to_user_id ? 'Assigned user' : 'Unassigned'
}

function formatVisitContext(action: {
  visit?: { visit_type?: string | null; status?: string | null; facility_id?: string | null } | null
}): string {
  if (!action.visit) return 'No linked visit context'
  const type = action.visit.visit_type ? `${formatToken(action.visit.visit_type)} visit` : 'Visit'
  const status = action.visit.status ? `, ${formatToken(action.visit.status)}` : ''
  const facility = action.visit.facility_id ? ', linked facility' : ''
  return `${type}${status}${facility}`
}

export function ActionDetailsPage() {
  const { actionId } = useParams()
  const { viewerContext } = useAuth()
  const [ownerRole, setOwnerRole] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [status, setStatus] = useState<CorrectiveActionStatus>('open')
  const [evidenceIdInput, setEvidenceIdInput] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [isActing, setIsActing] = useState(false)

  const { data: action, isLoading, error, refetch } = useQuery({
    queryKey: ['corrective-action', actionId],
    queryFn: () => getCorrectiveAction(actionId!),
    enabled: !!actionId,
  })

  useEffect(() => {
    if (!action) return
    setOwnerRole(action.owner_role || '')
    setAssignedTo(action.assigned_to_user_id || '')
    setDueDate(action.due_date || '')
    setStatus(action.status)
  }, [action])

  if (isLoading) return <div className="p-10 text-sm text-slate-500">Loading corrective action...</div>
  if (error || !action) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-semibold">Unable to load corrective action</p>
        <p className="text-sm">{(error as Error | undefined)?.message || 'Action not found'}</p>
        <Link className="mt-2 inline-block text-sm underline" to="/actions">
          Back to corrective actions
        </Link>
      </div>
    )
  }

  const viewerRole = viewerContext?.role || 'Surveyor'
  const viewerUserId = viewerContext?.user_id
  const hasPermission = canMutateAction(viewerRole, viewerUserId, action)
  const mutationLocked = isActionMutationLocked(action)
  const disableMutations = isActing || !hasPermission || mutationLocked
  const lockReason = mutationLocked
    ? 'Mutation locked: action is verified or visit is released/archived.'
    : !hasPermission
      ? 'You can only mutate assigned actions unless you are an Admin.'
      : null

  const submitUpdate = async () => {
    setActionError(null)
    setActionSuccess(null)
    setIsActing(true)
    try {
      const requestedStatus = (status || action.status) as CorrectiveActionStatus
      if (!canSetActionStatus(viewerRole, requestedStatus)) {
        throw new Error('Only Admin can set status to verified.')
      }

      await updateCorrectiveAction(action.action_id, {
        owner_role: ownerRole || null,
        assigned_to_user_id: assignedTo || null,
        due_date: dueDate || null,
        status: requestedStatus,
      })
      setActionSuccess('Corrective action updated.')
      await refetch()
    } catch (err) {
      setActionError((err as Error).message)
    } finally {
      setIsActing(false)
    }
  }

  const submitEvidenceLink = async () => {
    setActionError(null)
    setActionSuccess(null)
    setIsActing(true)
    try {
      await linkEvidenceToAction(action.action_id, evidenceIdInput.trim())
      setEvidenceIdInput('')
      setActionSuccess('Evidence linked to corrective action.')
      await refetch()
    } catch (err) {
      setActionError((err as Error).message)
    } finally {
      setIsActing(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
              Corrective action detail
            </p>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">{action.title}</h1>
            <p className="mt-1 break-words text-sm text-slate-600">
              Assigned to {formatAssignee(action)}. {formatVisitContext(action)}.
            </p>
          </div>
          <span className={action.status === 'verified' ? 'badge-released' : 'badge-in-progress'}>{action.status}</span>
        </div>
      </header>

      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Action Summary</h2>
        </div>
        <div className="grid gap-4 p-5 md:grid-cols-2">
          <div className="md:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">What this action is for</p>
            <p className="mt-1 text-sm leading-6 text-slate-700">
              {action.description || 'No description has been added for this corrective action.'}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assignee</p>
            <p className="mt-1 break-words text-sm font-semibold text-slate-800">{formatAssignee(action)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Owner role</p>
            <p className="mt-1 text-sm font-semibold text-slate-800">{formatToken(action.owner_role)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Due date</p>
            <p className="mt-1 text-sm font-semibold text-slate-800">{action.due_date || 'Not set'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Visit context</p>
            <p className="mt-1 text-sm font-semibold text-slate-800">{formatVisitContext(action)}</p>
          </div>
          {action.notes && (
            <div className="md:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</p>
              <p className="mt-1 text-sm leading-6 text-slate-700">{action.notes}</p>
            </div>
          )}
        </div>
      </section>

      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Edit</h2>
          {lockReason && <span className="text-xs font-semibold text-slate-500">Guarded by action/visit state</span>}
        </div>
        <div className="grid gap-3 p-5 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Owner role</span>
            <select
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              disabled={disableMutations}
              value={ownerRole}
              onChange={(e) => setOwnerRole(e.target.value)}
            >
              <option value="">Unassigned</option>
              {OWNER_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Assignee</span>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
              {formatAssignee(action)}
            </div>
            <details className="mt-2 rounded-lg border border-dashed border-slate-200 px-3 py-2">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">
                Advanced assignment ID
              </summary>
              <input
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={assignedTo}
                disabled={disableMutations}
                onChange={(e) => setAssignedTo(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">
                Use only when changing the assignment to a known user UUID.
              </p>
            </details>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Due date</span>
            <input
              type="date"
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              value={dueDate}
              disabled={disableMutations}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Status</span>
            <select
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              disabled={disableMutations}
              value={status}
              onChange={(e) => setStatus(e.target.value as CorrectiveActionStatus)}
            >
              {STATUSES.map((item) => (
                <option key={item} value={item} disabled={!canSetActionStatus(viewerRole, item)}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="border-t border-slate-100 px-5 py-4">
          <button className="btn-primary text-sm" disabled={disableMutations} onClick={submitUpdate}>
            Save updates
          </button>
          {lockReason && <p className="mt-2 text-xs text-slate-500">{lockReason}</p>}
        </div>
      </section>

      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Evidence Links</h2>
        </div>
        <div className="p-5">
          <p className="text-xs text-slate-500">
            Evidence links are visible below. Linking is mutation-locked when action/visit state is immutable.
          </p>
          {action.evidence_links.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">No evidence linked yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {action.evidence_links.map((link) => (
                <li key={link.action_evidence_link_id} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  Evidence: <span className="font-semibold">{link.evidence_id}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t border-slate-100 px-5 py-4 md:flex-row md:items-center">
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm md:w-72"
            placeholder="Evidence ID"
            value={evidenceIdInput}
            disabled={disableMutations}
            onChange={(e) => setEvidenceIdInput(e.target.value)}
          />
          <button
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disableMutations || !evidenceIdInput.trim()}
            onClick={submitEvidenceLink}
          >
            Link evidence
          </button>
        </div>
      </section>

      {actionError && <p className="text-sm text-red-600">{actionError}</p>}
      {actionSuccess && <p className="text-sm text-emerald-700">{actionSuccess}</p>}
    </div>
  )
}
